import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Image,
  Container,
  Card,
  Text,
  Group,
  Button,
  Divider,
  Title,
  Stack,
  Grid,
  TextInput,
  Checkbox,
  Skeleton,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconCalendar, IconMapPin } from "@tabler/icons-react";
import { useLocation } from "react-router-dom";

const CheckoutPage = () => {
  const [loading, setLoading] = useState(false);
  const [eventDetails, setEventDetails] = useState([]);
  const location = useLocation();
  const { tickets } = location.state || { tickets: [] };
  const [calculation, setCalculation] = useState(null);

  const ticketForm = useForm({
    initialValues: {
      tickets: tickets.map((ticket) => ({
        ...ticket,
        count: ticket.count || 0,
      })),
      email: "",
      phoneNumber: "",
    },
  });

  //event details
  useEffect(() => {
    const getEventDetails = async () => {
      try {
        const response = await axios.get(
          "https://api.dev.fastbreakplatform.com/ticketing/website/info"
        );

        setEventDetails(response.data);
      } catch (error) {
        console.error("Error fetching event details:", error);
      } finally {
        setLoading(false);
      }
    };

    getEventDetails();
  }, []);

  //useEffect for Initial Load
  useEffect(() => {
    calculateCart();
  }, []);

  //Cart Calculation Function

  const calculateCart = async () => {
    const lineItems = ticketForm
      .getValues()
      .tickets.filter((ticket) => ticket.count > 0)
      .map((ticket) => ({
        ticketId: ticket.id,
        qty: ticket.count,
      }));

    if (!lineItems.length) {
      setCalculation(null);
      return;
    }

    try {
      const response = await axios.post(
        "https://api.dev.fastbreakplatform.com/ticketing/cart/calculate",
        {
          lineItems,
          eventId: eventDetails?.id || "7", //currently hard coded to 7 since there is no eventId in event details.
        }
      );

      setCalculation(response.data);
    } catch (error) {
      console.error("Error calculating cart:", error);
    }
  };

  const addTicket = (index) => {
    const tickets = ticketForm.getValues().tickets;
    ticketForm.setFieldValue(
      `tickets.${index}.count`,
      tickets[index].count + 1
    );
    calculateCart();
  };

  const minusTicket = (index) => {
    const tickets = ticketForm.getValues().tickets;
    ticketForm.setFieldValue(
      `tickets.${index}.count`,
      Math.max(0, tickets[index].count - 1)
    );
    calculateCart();
  };

  //Stripe Function
  const submitTicket = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const { email, phoneNumber } = ticketForm.getValues();

      const lineItems = ticketForm
        .getValues()
        .tickets.filter((ticket) => ticket.count > 0)
        .map((ticket) => ({
          ticketId: ticket.id,
          qty: ticket.count,
        }));

      if (!lineItems.length) {
        console.error("No tickets selected.");
        setLoading(false);
        return;
      }

      const response = await axios.post(
        "https://api.dev.fastbreakplatform.com/ticketing/cart/checkout",
        {
          lineItems,
          eventId: eventDetails?.id || "7", //Currently Hard Coded to 7 since there is no eventId in event details.
          email,
          phoneNumber,
        }
      );

      if (response.data?.code === "SUCCESS" && response.data?.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error("Unexpected response format or missing checkout URL.");
      }
    } catch (error) {
      console.error("Error during checkout:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Skeleton visible={loading} />;
  }

  return (
    <Container size="lg" style={{ marginTop: 30 }}>
      {/* Banner Image */}
      <Image
        src={
          eventDetails?.websiteBannerURL ??
          "https://www.sportstravelmagazine.com/wp-content/uploads/2024/11/Fastbreak.ai_.jpg"
        }
        radius="md"
        className="banner-image"
      />

      {/* Ticket Selector */}
      <Grid style={{ marginTop: 30 }}>
        <Grid.Col span={{ base: 12, sm: 4 }} order={{ base: 1, sm: 2 }}>
          <form onSubmit={submitTicket}>
            <Card
              shadow="lg"
              className="ticket-card"
              radius={15}
              style={{
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
              }}
            >
              <Group>
                {/* Event Image */}
                <Image
                  src={
                    eventDetails?.logoURL ??
                    "https://pbs.twimg.com/profile_images/1692257149194293248/1SAmbuUS_400x400.jpg"
                  }
                  width={120}
                  height={120}
                  style={{ objectFit: "cover" }}
                />

                {/* Event Details */}
                <div style={{ flex: 1 }}>
                  <Title order={3} size={15} style={{ marginBottom: "8px" }}>
                    {eventDetails?.name ?? "Event Name Unavailable"}
                  </Title>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <Group spacing={8} align="center">
                      <IconCalendar size={16} />
                      <Text size="xs">
                        {eventDetails?.formattedSchedule ??
                          "Event Date Unavailable"}
                      </Text>
                    </Group>
                    <Group spacing={8} align="center">
                      <IconMapPin size={16} />
                      <Text size="xs">
                        {eventDetails?.venue?.name ?? "Event Venue Unavailable"}
                      </Text>
                    </Group>
                  </div>
                </div>
              </Group>

              <Divider my="sm" />
              {/* Ticket Selector */}
              {ticketForm.values.tickets.map((ticket, index) => (
                <div
                  key={ticket.id}
                  style={{
                    padding: "16px",
                  }}
                >
                  <Text size="md" mb="md">
                    Event Tickets
                  </Text>
                  <Group position="apart" align="center" noWrap>
                    <div style={{ flex: 1 }}>
                      <Text
                        weight={600}
                        size="sm"
                        style={{ marginBottom: "4px" }}
                      >
                        {ticket.name ?? "Ticket Name Unavailable"}
                      </Text>
                      <Text
                        size="xs"
                        c="dimmed"
                        style={{ marginBottom: "4px" }}
                      >
                        {ticket.price ?? "Price Unavailable"} + fees
                      </Text>
                      {ticket.description && (
                        <Text size="xs" c="dimmed">
                          {ticket.description}
                        </Text>
                      )}
                    </div>
                    <Group spacing={8} align="center">
                      <Button
                        size="xs"
                        color="gray"
                        variant="outline"
                        onClick={() => minusTicket(index)}
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        -
                      </Button>
                      <Text
                        size="sm"
                        weight={500}
                        style={{ textAlign: "center" }}
                      >
                        {ticket.count ?? 0}
                      </Text>
                      <Button
                        size="xs"
                        color="blue"
                        onClick={() => addTicket(index)}
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        +
                      </Button>
                    </Group>
                  </Group>
                  <Divider my="sm" />
                </div>
              ))}

              {calculation && (
                <Card
                  style={{
                    padding: "16px",
                  }}
                >
                  {ticketForm.getValues().tickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <Text size="sm">
                        {ticket.name} ({ticket.count}x)
                      </Text>
                      <Text size="sm">{ticket.price}</Text>
                    </div>
                  ))}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <Text size="sm">Venue Fees</Text>
                    <Text size="sm">{calculation.fees?.formattedValue}</Text>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <Text size="sm">Tax</Text>
                    <Text size="sm">{calculation.fees?.formattedValue}</Text>
                  </div>
                  <Divider my="sm" />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontWeight: 700,
                      marginBottom: "8px",
                    }}
                  >
                    <Text size="md" weight={700}>
                      Total
                    </Text>
                    <Text size="md" weight={700}>
                      {calculation.grandTotal?.formattedValue}
                    </Text>
                  </div>
                  <Button
                    fullWidth
                    color="green"
                    size="lg"
                    style={{
                      borderRadius: "8px",
                      height: "40px",
                      fontSize: "16px",
                    }}
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? "Processing..." : "Place Order"}
                  </Button>
                </Card>
              )}
            </Card>
          </form>
        </Grid.Col>

        {/* Where to Send Tickets Section */}
        <Grid.Col span={{ base: 12, sm: 8 }} order={{ base: 2, sm: 1 }}>
          {/* Ticket Form */}
          <Title order={4} mt="xl">
            Where should we send your tickets?
          </Title>
          <form onSubmit={submitTicket}>
            <TextInput
              label="Email"
              placeholder="john.doe@example.com"
              mt="lg"
              {...ticketForm.getInputProps("email")}
            />
            <TextInput
              label="Phone number"
              placeholder="(123) 456-7890"
              mt="lg"
              {...ticketForm.getInputProps("phoneNumber")}
            />

            <Checkbox
              label="Yes, it's okay to send me text messages including confirmations, changes, and updates."
              mt="lg"
            />
            <Text size="xs" c="gray">
              Message frequency varies. Message and data rates may apply.
            </Text>
            <Checkbox
              label={
                <>
                  I agree to{" "}
                  <a href="#" style={{ color: "blue" }}>
                    Terms of Service & Privacy Policy
                  </a>
                  .
                </>
              }
              mt="lg"
            />
          </form>
        </Grid.Col>
      </Grid>

      {/* Footer Text */}
      <Text size="xs" mt={50}>
        Powered by <strong>FASTBREAK</strong>
      </Text>
    </Container>
  );
};

export default CheckoutPage;
